/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { restaurantReadiness, setRestaurantFdMerchantId } from '../apollo'
import {
  Box, Container, Typography, Paper, InputBase,
  CircularProgress, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import EditIcon from '@mui/icons-material/Edit'

const GET_READINESS = gql`${restaurantReadiness}`
const SET_FD_MERCHANT_ID = gql`${setRestaurantFdMerchantId}`

// The columns of the checklist. `key` maps to the boolean field on each row.
// `help` explains what a red X means and how to fix it.
const CHECKS = [
  { key: 'isActive', label: 'Active', help: 'Restaurant must be enabled (Restaurants page → Enable). Disabled = never shown.' },
  { key: 'hasLocation', label: 'Location', help: 'Address pin with latitude & longitude must be set. Without it the restaurant falls outside the discovery radius.' },
  { key: 'hasDeliveryZone', label: 'Delivery zone', help: 'Platform-delivery: an admin delivery zone must cover the area. Self-delivery: own zone(s) or none-yet both keep it listed.' },
  { key: 'hasMenu', label: 'Menu', help: 'At least one active menu with items. Not required to appear, but customers cannot order without it.' },
  { key: 'hasOpeningHours', label: 'Open hours', help: 'Opening times must be set, otherwise the restaurant always shows as Closed.' },
  { key: 'hasCommission', label: 'Commission', help: 'A commission rate must be set (> 0). Set it on the Commission page. All rates at 0/blank = not configured.' },
  { key: 'hasFirstDeliveryMerchantId', label: 'FD Merchant', help: 'First Delivery Merchant ID for dispatch integration. Set it per restaurant in the FD Merchant ID column.' },
]

const Tick = ({ ok }) => (
  ok
    ? <CheckCircleIcon sx={{ fontSize: 20, color: '#16a34a' }} />
    : <CancelIcon sx={{ fontSize: 20, color: '#ef4444' }} />
)

const RestaurantReadiness = props => {
  const [searchQuery, setSearchQuery] = useState('')
  const [onlyIssues, setOnlyIssues] = useState(false)

  // FD Merchant ID edit dialog
  const [fdDialog, setFdDialog] = useState(null) // { _id, name, value }
  const [fdInput, setFdInput] = useState('')
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })

  const { data, loading, refetch } = useQuery(GET_READINESS, {
    fetchPolicy: 'network-only',
  })

  const [saveFd, { loading: savingFd }] = useMutation(SET_FD_MERCHANT_ID)

  const openFdDialog = (r) => { setFdDialog(r); setFdInput(r.firstDeliveryMerchantId || '') }
  const closeFdDialog = () => setFdDialog(null)
  const submitFd = () => {
    saveFd({
      variables: { restaurantId: fdDialog._id, firstDeliveryMerchantId: fdInput.trim() || null },
      onCompleted: () => {
        setSnack({ open: true, severity: 'success', msg: `Saved FD Merchant ID for ${fdDialog.name}` })
        closeFdDialog()
        refetch()
      },
      onError: (e) => setSnack({ open: true, severity: 'error', msg: e?.graphQLErrors?.[0]?.message || 'Failed to save' }),
    })
  }

  const rows = data?.restaurantReadiness ?? []

  const visibleCount = rows.filter(r => r.visibleOnWeb).length
  const hiddenCount = rows.length - visibleCount

  const filtered = useMemo(() => {
    let list = rows
    if (onlyIssues) {
      list = list.filter(r =>
        !r.visibleOnWeb || CHECKS.some(c => !r[c.key])
      )
    }
    if (searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase()
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.address || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [rows, searchQuery, onlyIssues])

  return (
    <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StorefrontIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#0f172a', lineHeight: 1.2 }}>Restaurant Readiness</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
              Which restaurants meet the requirements to appear in the customer app
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', px: 2, py: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d4ed8' }}>{loading ? '…' : rows.length}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>Total</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', px: 2, py: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>{loading ? '…' : visibleCount}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Visible</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', px: 2, py: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#dc2626' }}>{loading ? '…' : hiddenCount}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Hidden</Typography>
          </Box>
          <IconButton onClick={() => refetch()} size="small" sx={{ ml: 0.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <RefreshIcon sx={{ fontSize: 18, color: '#64748b' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Search + filter row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', px: 2, py: 1, flex: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
          <InputBase
            placeholder="Search by name or address..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }}
          />
        </Box>
        <Chip
          onClick={() => setOnlyIssues(v => !v)}
          label={onlyIssues ? 'Showing issues only' : 'Show issues only'}
          variant={onlyIssues ? 'filled' : 'outlined'}
          sx={{
            cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', height: 40, px: 1,
            borderRadius: '12px',
            bgcolor: onlyIssues ? '#fef2f2' : '#fff',
            color: onlyIssues ? '#dc2626' : '#64748b',
            borderColor: onlyIssues ? '#fecaca' : '#e2e8f0',
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <StorefrontIcon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
          <Typography sx={{ color: '#94a3b8' }}>No restaurants found</Typography>
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1080 }}>
              {/* Table header */}
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', px: 2, py: 1.4 }}>
                <Box sx={{ flex: '1 1 260px', minWidth: 220 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Restaurant</Typography>
                </Box>
                {CHECKS.map(c => (
                  <Box key={c.key} sx={{ flex: '0 0 96px', textAlign: 'center' }}>
                    <Tooltip title={c.help} arrow>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'help' }}>{c.label}</Typography>
                    </Tooltip>
                  </Box>
                ))}
                <Box sx={{ flex: '0 0 210px', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>FD Merchant ID</Typography>
                </Box>
                <Box sx={{ flex: '0 0 120px', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>On Web</Typography>
                </Box>
              </Box>

              {/* Rows */}
              {filtered.map((r, idx) => (
                <Box
                  key={r._id}
                  onClick={() => {
                    localStorage.setItem('restaurantId', r._id)
                    localStorage.setItem('restaurantName', r.name)
                    if (r.image) localStorage.setItem('restaurantImage', r.image)
                    props.history.push('/admin/dashboard/' + r.slug)
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 2, py: 1.3, cursor: 'pointer',
                    borderBottom: idx === filtered.length - 1 ? 'none' : '1px solid #f1f5f9',
                    bgcolor: r.visibleOnWeb ? '#fff' : '#fffbfa',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  {/* Restaurant name + address */}
                  <Box sx={{ flex: '1 1 260px', minWidth: 220, display: 'flex', alignItems: 'center', gap: 1.2, pr: 2 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {r.logo || r.image
                        ? <img src={r.logo || r.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <StorefrontIcon sx={{ fontSize: 20, color: '#cbd5e1' }} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</Typography>
                        {r.selfDelivery && (
                          <Chip label="Self" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#eef2ff', color: '#6366f1' }} />
                        )}
                      </Box>
                      {r.address && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.address}</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Check ticks */}
                  {CHECKS.map(c => (
                    <Box key={c.key} sx={{ flex: '0 0 96px', display: 'flex', justifyContent: 'center' }}>
                      <Tick ok={!!r[c.key]} />
                    </Box>
                  ))}

                  {/* FD Merchant ID — editable (super admin sets it here) */}
                  <Box
                    sx={{ flex: '0 0 210px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 0.5 }}
                    onClick={(e) => { e.stopPropagation(); openFdDialog(r) }}
                  >
                    {r.firstDeliveryMerchantId ? (
                      <Tooltip title={r.firstDeliveryMerchantId} arrow>
                        <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontFamily: 'monospace', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                          {r.firstDeliveryMerchantId}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', fontStyle: 'italic', cursor: 'pointer' }}>Not set</Typography>
                    )}
                    <Tooltip title="Set / edit FD Merchant ID">
                      <IconButton size="small" sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Overall visibility */}
                  <Box sx={{ flex: '0 0 120px', display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      icon={r.visibleOnWeb
                        ? <VisibilityIcon sx={{ fontSize: '15px !important', color: '#16a34a !important' }} />
                        : <VisibilityOffIcon sx={{ fontSize: '15px !important', color: '#dc2626 !important' }} />}
                      label={r.visibleOnWeb ? 'Visible' : 'Hidden'}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.72rem', height: 24,
                        bgcolor: r.visibleOnWeb ? '#dcfce7' : '#fee2e2',
                        color: r.visibleOnWeb ? '#16a34a' : '#dc2626',
                        '& .MuiChip-icon': { ml: '6px' },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>A restaurant is Visible on the web only when Active + Location + Delivery zone all pass. Menu, Open hours, Commission &amp; FD Merchant ID are recommended but don’t hide it.</Typography>
      </Box>

      {/* FD Merchant ID edit dialog */}
      <Dialog open={!!fdDialog} onClose={closeFdDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
          First Delivery Merchant ID
          {fdDialog && <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 500 }}>{fdDialog.name}</Typography>}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mb: 1.5 }}>
            Internal dispatch integration ID. Merchants don’t see this — set it here per restaurant. Leave blank to clear.
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="e.g. a6952531-26be-11f1-badd-3ff6d4a0922f"
            value={fdInput}
            onChange={(e) => setFdInput(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFdDialog} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button
            onClick={submitFd}
            disabled={savingFd}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {savingFd ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default withTranslation()(RestaurantReadiness)
